import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuthStore } from '../store/auth';
import { useUIStore } from '../store/ui';

interface Category {
  id: string;
  name: string;
  icon: string;
  children?: Category[];
}

export default function CreateListing() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showToast, setLoading } = useUIStore();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    price: '',
    currency: 'RUB',
    images: [] as string[]
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  useEffect(() => {
    loadCategories();
    setupMainButton();
    
    return () => {
      window.Telegram?.WebApp?.MainButton.hide();
    };
  }, []);

  useEffect(() => {
    updateMainButton();
  }, [formData]);

  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories('listing');
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const setupMainButton = () => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    tg.MainButton.setParams({
      text: 'Опубликовать',
      color: '#2481cc'
    });
    tg.MainButton.onClick(handleSubmit);
  };

  const updateMainButton = () => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    const isValid = formData.title.length >= 3 && 
                    formData.description.length >= 10 && 
                    formData.category;

    if (isValid) {
      tg.MainButton.enable();
      tg.MainButton.show();
    } else {
      tg.MainButton.disable();
      tg.MainButton.hide();
    }
  };

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
    setFormData({ ...formData, category: category.id, subcategory: '' });
  };

  const handleSubmit = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const data = {
        userId: user.id,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory || undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        currency: formData.currency,
        images: formData.images,
        countryCode: user.countryCode,
        regionId: user.regionId || 1
      };

      await apiService.createListing(data);
      
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      showToast('Объявление опубликовано!', 'success');
      navigate('/listings');
    } catch (error) {
      console.error('Error creating listing:', error);
      showToast('Ошибка при создании объявления', 'error');
    } finally {
      setLoading(false);
    }
  };

  const currencies = [
    { code: 'RUB', symbol: '₽', label: 'Рубль' },
    { code: 'KZT', symbol: '₸', label: 'Тенге' },
    { code: 'BYN', symbol: 'Br', label: 'Бел. рубль' }
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">Новое объявление</h1>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-1">Название *</label>
        <input
          type="text"
          placeholder="Что вы продаёте?"
          className="input"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          maxLength={200}
        />
        <p className="text-xs text-tg-theme-hint mt-1">
          {formData.title.length}/200
        </p>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium mb-1">Категория *</label>
        <div className="grid grid-cols-3 gap-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategorySelect(cat)}
              className={`p-3 rounded-lg text-center text-sm ${
                formData.category === cat.id
                  ? 'bg-tg-theme-button text-tg-theme-button-text'
                  : 'bg-tg-theme-secondary-bg'
              }`}
            >
              <span className="text-xl block mb-1">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Subcategory */}
      {selectedCategory?.children && selectedCategory.children.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-1">Подкатегория</label>
          <div className="flex flex-wrap gap-2">
            {selectedCategory.children.map((sub) => (
              <button
                key={sub.id}
                onClick={() => setFormData({ ...formData, subcategory: sub.id })}
                className={`px-3 py-2 rounded-full text-sm ${
                  formData.subcategory === sub.id
                    ? 'bg-tg-theme-button text-tg-theme-button-text'
                    : 'bg-tg-theme-secondary-bg'
                }`}
              >
                {sub.icon} {sub.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1">Описание *</label>
        <textarea
          placeholder="Опишите ваш товар подробнее..."
          className="input min-h-32"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          maxLength={5000}
        />
        <p className="text-xs text-tg-theme-hint mt-1">
          {formData.description.length}/5000
        </p>
      </div>

      {/* Price */}
      <div>
        <label className="block text-sm font-medium mb-1">Цена</label>
        <div className="flex gap-2">
          <input
            type="number"
            placeholder="0"
            className="input flex-1"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          />
          <select
            className="input w-24"
            value={formData.currency}
            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
          >
            {currencies.map((cur) => (
              <option key={cur.code} value={cur.code}>
                {cur.symbol}
              </option>
            ))}
          </select>
        </div>
        <p className="text-xs text-tg-theme-hint mt-1">
          Оставьте пустым, если цена договорная
        </p>
      </div>

      {/* Images placeholder */}
      <div>
        <label className="block text-sm font-medium mb-1">Фотографии</label>
        <div className="card text-center py-8">
          <span className="text-4xl block mb-2">📷</span>
          <p className="text-sm text-tg-theme-hint">
            Загрузка фото будет доступна в следующей версии
          </p>
        </div>
      </div>

      {/* Submit hint */}
      <p className="text-sm text-tg-theme-hint text-center">
        Нажмите кнопку "Опубликовать" внизу экрана
      </p>
    </div>
  );
}
